export enum ReportType {
  LEAVE_ABSENCE_DIGEST = "LEAVE_ABSENCE_DIGEST",
  MONTHLY_WHEREABOUTS_SUMMARY = "MONTHLY_WHEREABOUTS_SUMMARY",
  MONTHLY_OFFICE_SUMMARY = "MONTHLY_OFFICE_SUMMARY",
  MISSING_WHEREABOUTS_SUMMARY = "MISSING_WHEREABOUTS_SUMMARY",
  VISITOR_SUMMARY = "VISITOR_SUMMARY",
  /** Saved absence reports. Only used to key the "send me a sample" cooldown. */
  ABSENCE_REPORT = "ABSENCE_REPORT",
}

export enum ReportRecipientType {
  LINE_MANAGERS = "LINE_MANAGERS",
  TEAM_ADMINS = "TEAM_ADMINS",
  ALL_TEAM_MEMBERS = "ALL_TEAM_MEMBERS",
  COMPANY_ADMINS = "COMPANY_ADMINS",
  OFFICE_ADMINS = "OFFICE_ADMINS",
}

/** When a weekly report is emailed. Monthly reports have no schedule. */
export interface ReportSchedule {
  sendDay: number;    // ISO day-of-week, 1 = Monday .. 7 = Sunday
  sendTime: string;   // "HH:mm" wall-clock time in timezoneId
  timezoneId: string; // IANA id, e.g. "Europe/London"
}

export interface ReportConfig {
  reportType: ReportType;
  enabled: boolean;
  recipientTypes?: ReportRecipientType[] | null;
  sendDay?: number | null;
  sendTime?: string | null;
  timezoneId?: string | null;
}

/**
 * Mirrors the backend defaults (WeeklyReportSchedule.defaultFor): the fixed schedule each weekly
 * report was sent on before the day and time became configurable.
 */
export function defaultReportSchedule(reportType: ReportType): ReportSchedule {
  return {
    sendDay: reportType === ReportType.VISITOR_SUMMARY ? 5 : 1,
    sendTime: '07:00',
    timezoneId: 'Europe/London',
  };
}

export function scheduleFromConfig(config: ReportConfig | undefined, reportType: ReportType): ReportSchedule {
  const defaults = defaultReportSchedule(reportType);
  return {
    sendDay: config?.sendDay ?? defaults.sendDay,
    sendTime: config?.sendTime ?? defaults.sendTime,
    timezoneId: config?.timezoneId ?? defaults.timezoneId,
  };
}

/**
 * How often a saved absence report is emailed: daily (Monday to Friday, covering that day), weekly
 * (on sendDay, covering the coming Monday to Sunday) or monthly (on the 1st, covering that month).
 */
export enum AbsenceReportFrequency {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
}

export interface AbsenceReportRecipient {
  userId: string;
  firstName: string;
  lastName: string;
}

/** A saved "who is not in work" report, emailed to chosen people for one or more departments. */
export interface ScheduledAbsenceReport {
  id?: number;
  name: string;
  enabled: boolean;
  frequency: AbsenceReportFrequency;
  sendDay: number;    // ISO day-of-week, weekly reports only
  sendTime: string;   // "HH:mm" wall-clock time in timezoneId
  timezoneId: string; // IANA id
  departmentIds: number[];
  recipientUserIds: string[];
  recipients?: AbsenceReportRecipient[];
}
